import React, { useCallback, useEffect, useState } from "react";
import PortalAdminLayout from "./PortalAdminLayout";
import {
  FaCheckCircle,
  FaEye,
  FaTrash,
  FaList,
  FaFlag,
  FaShieldAlt,
  FaRedo,
  FaUsers,
  FaChartBar,
  FaClock,
  FaExclamationTriangle,
  FaArrowUp,
  FaCheck,
  FaTimes,
  FaDownload,
  FaGavel,
} from "react-icons/fa";

const QUEUE_TABS = [
  {
    key: "reported",
    label: "New Reports",
    icon: <FaFlag />,
    queueParam: "new_report",
  },
  {
    key: "under_review",
    label: "Under Review",
    icon: <FaEye />,
    queueParam: "under_review",
  },
  {
    key: "escalated",
    label: "Escalated",
    icon: <FaArrowUp />,
    queueParam: "escalated",
  },
  {
    key: "resolved",
    label: "Resolved",
    icon: <FaCheck />,
    queueParam: "resolved",
  },
  {
    key: "deleted",
    label: "Deleted",
    icon: <FaTrash />,
    queueParam: "deleted",
  },
  { key: "appeals", label: "Appeals", icon: <FaGavel /> },
  { key: "all", label: "All Looks", icon: <FaList /> },
  { key: "providers", label: "Provider Directory", icon: <FaUsers /> },
  { key: "analytics", label: "Analytics", icon: <FaChartBar /> },
];

const QUEUE_STATUS_LABELS = {
  new_report: { label: "New Report", color: "#ef4444", bg: "#fee2e2" },
  under_review: { label: "Under Review", color: "#3b82f6", bg: "#eff6ff" },
  escalated: { label: "Escalated", color: "#7c3aed", bg: "#f5f3ff" },
  resolved: { label: "Resolved", color: "#22c55e", bg: "#f0fdf4" },
  deleted: { label: "Deleted", color: "#6b7280", bg: "#f3f4f6" },
};

const APPEAL_STATUS_LABELS = {
  pending: { label: "Pending", color: "#f0a500", bg: "#fffbeb" },
  approved: { label: "Approved", color: "#22c55e", bg: "#f0fdf4" },
  denied: { label: "Denied", color: "#ef4444", bg: "#fee2e2" },
  withdrawn: { label: "Withdrawn", color: "#6b7280", bg: "#f9fafb" },
};

const QUEUE_MOVE_OPTIONS = [
  { value: "under_review", label: "→ Under Review" },
  { value: "escalated", label: "→ Escalate" },
  { value: "resolved", label: "→ Resolve" },
];

function getSlaInfo(slaDueAt) {
  if (!slaDueAt) return null;
  const diffMs = new Date(slaDueAt).getTime() - Date.now();
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours < 0)
    return {
      label: `Overdue ${Math.abs(Math.round(diffHours))}h`,
      color: "#ef4444",
      isOverdue: true,
    };
  if (diffHours < 24)
    return {
      label: `${Math.round(diffHours)}h left`,
      color: "#f0a500",
      isOverdue: false,
    };
  return {
    label: `${Math.round(diffHours / 24)}d left`,
    color: "#6b7280",
    isOverdue: false,
  };
}

const STATUS_LABELS = {
  draft: { label: "Draft", color: "#888" },
  awaiting_consent: { label: "Awaiting Consent", color: "#f0a500" },
  ready: { label: "Ready", color: "#3b82f6" },
  published: { label: "Published", color: "#22c55e" },
  archived: { label: "Archived", color: "#6b7280" },
  deleted: { label: "Deleted", color: "#dc2626" },
};

const ENFORCE_ACTIONS = [
  {
    key: "dismiss_report",
    label: "Dismiss Report",
    color: "#374151",
    bg: "#f9fafb",
    border: "#d1d5db",
  },
  {
    key: "hide_look",
    label: "Hide Look",
    color: "#92400e",
    bg: "#fffbeb",
    border: "#fcd34d",
  },
  {
    key: "restore_look",
    label: "Restore Look",
    color: "#166534",
    bg: "#f0fdf4",
    border: "#86efac",
  },
  {
    key: "warn",
    label: "Warn Provider",
    color: "#92400e",
    bg: "#fffbeb",
    border: "#fcd34d",
  },
  {
    key: "restrict",
    label: "Restrict Provider",
    color: "#b91c1c",
    bg: "#fef2f2",
    border: "#fca5a5",
  },
  {
    key: "suspend",
    label: "Suspend Provider",
    color: "#7f1d1d",
    bg: "#fef2f2",
    border: "#f87171",
  },
];

function LooksModeration() {
  const [activeTab, setActiveTab] = useState("reported");
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [reasons, setReasons] = useState({});
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [selectedLook, setSelectedLook] = useState(null);
  const [enforceAction, setEnforceAction] = useState(null);
  const [enforceNote, setEnforceNote] = useState("");
  const [enforcing, setEnforcing] = useState(false);
  const [queueMovingId, setQueueMovingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tabCounts, setTabCounts] = useState({});

  // Appeals
  const [appeals, setAppeals] = useState([]);
  const [appealsLoading, setAppealsLoading] = useState(false);
  const [appealsError, setAppealsError] = useState("");
  const [appealDecision, setAppealDecision] = useState(null);
  const [appealResponse, setAppealResponse] = useState("");
  const [appealBusy, setAppealBusy] = useState(false);

  // Provider Directory
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState("");

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");

  // Permanent Delete Confirmation
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState(null); // { type: 'single', lookId } or { type: 'bulk', lookIds: [] }

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      window.location.href = "/login";
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  const getApiBaseUrl = useCallback(
    () => process.env.REACT_APP_API_BASE_URL || "",
    [],
  );

  const loadLooks = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    try {
      setLoading(true);
      setError("");
      const tab = QUEUE_TABS.find((t) => t.key === activeTab);
      let url;
      if (activeTab === "all") {
        url = `${baseURL}/api/looks/admin/all?limit=100`;
      } else if (tab?.queueParam) {
        url = `${baseURL}/api/looks/admin/pending?queue=${tab.queueParam}&sort=${sortBy}`;
      } else {
        setLoading(false);
        return;
      }
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load looks");
      setLooks(data.looks || []);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || "Failed to load looks");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, activeTab, sortBy]);

  const loadAppeals = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    setAppealsLoading(true);
    setAppealsError("");
    try {
      const res = await fetch(`${baseURL}/api/looks/admin/appeals?status=all`, {
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load appeals");
      setAppeals(data.looks || []);
    } catch (err) {
      setAppealsError(err.message || "Failed to load appeals");
    } finally {
      setAppealsLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const loadProviders = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    setProvidersLoading(true);
    setProvidersError("");
    try {
      const res = await fetch(`${baseURL}/api/looks/admin/provider-directory`, {
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load providers");
      setProviders(data.providers || []);
    } catch (err) {
      setProvidersError(err.message || "Failed to load providers");
    } finally {
      setProvidersLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const loadAnalytics = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    setAnalyticsLoading(true);
    setAnalyticsError("");
    try {
      const res = await fetch(`${baseURL}/api/looks/admin/analytics`, {
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load analytics");
      setAnalytics(data);
    } catch (err) {
      setAnalyticsError(err.message || "Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const loadTabCounts = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;

    try {
      const [
        newReportRes,
        underReviewRes,
        escalatedRes,
        resolvedRes,
        deletedRes,
        allRes,
        pendingAppealsRes,
      ] = await Promise.all([
        fetch(`${baseURL}/api/looks/admin/pending?queue=new_report`, {
          headers,
        }),
        fetch(`${baseURL}/api/looks/admin/pending?queue=under_review`, {
          headers,
        }),
        fetch(`${baseURL}/api/looks/admin/pending?queue=escalated`, {
          headers,
        }),
        fetch(`${baseURL}/api/looks/admin/pending?queue=resolved`, { headers }),
        fetch(`${baseURL}/api/looks/admin/pending?queue=deleted`, { headers }),
        fetch(`${baseURL}/api/looks/admin/all?limit=1`, { headers }),
        fetch(`${baseURL}/api/looks/admin/appeals?status=pending`, { headers }),
      ]);

      const [
        newReportData,
        underReviewData,
        escalatedData,
        resolvedData,
        deletedData,
        allData,
        pendingAppealsData,
      ] = await Promise.all([
        newReportRes.json(),
        underReviewRes.json(),
        escalatedRes.json(),
        resolvedRes.json(),
        deletedRes.json(),
        allRes.json(),
        pendingAppealsRes.json(),
      ]);

      setTabCounts({
        reported: newReportRes.ok
          ? (newReportData.count ?? (newReportData.looks || []).length)
          : 0,
        under_review: underReviewRes.ok
          ? (underReviewData.count ?? (underReviewData.looks || []).length)
          : 0,
        escalated: escalatedRes.ok
          ? (escalatedData.count ?? (escalatedData.looks || []).length)
          : 0,
        resolved: resolvedRes.ok
          ? (resolvedData.count ?? (resolvedData.looks || []).length)
          : 0,
        deleted: deletedRes.ok
          ? (deletedData.count ?? (deletedData.looks || []).length)
          : 0,
        all: allRes.ok ? (allData.total ?? (allData.looks || []).length) : 0,
        appeals: pendingAppealsRes.ok
          ? (pendingAppealsData.count ??
            (pendingAppealsData.looks || []).length)
          : 0,
      });
    } catch (_) {
      // Non-blocking: counts badges are supplementary.
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const isLooksTab = QUEUE_TABS.filter(
    (t) => t.queueParam || t.key === "all",
  ).some((t) => t.key === activeTab);

  useEffect(() => {
    if (isLooksTab) loadLooks();
    else if (activeTab === "appeals") loadAppeals();
    else if (activeTab === "providers") loadProviders();
    else if (activeTab === "analytics") loadAnalytics();
    loadTabCounts();
  }, [
    activeTab,
    sortBy,
    isLooksTab,
    loadLooks,
    loadAppeals,
    loadProviders,
    loadAnalytics,
    loadTabCounts,
  ]);

  const moderateLook = useCallback(
    async (lookId, decision) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      const reason = (reasons[lookId] || "").trim();
      if (decision === "remove" && !reason) {
        setError("A reason is required when removing a look.");
        return;
      }
      setBusyId(lookId);
      setError("");
      try {
        const res = await fetch(`${baseURL}/api/looks/${lookId}/moderate`, {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(
            decision === "remove" ? { decision, reason } : { decision },
          ),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Action failed");
        setLooks((prev) => prev.filter((l) => l._id !== lookId));
        await loadTabCounts();
      } catch (err) {
        setError(err.message || "Action failed");
      } finally {
        setBusyId(null);
      }
    },
    [getAuthHeaders, getApiBaseUrl, reasons, loadTabCounts],
  );

  const applyEnforcement = useCallback(async () => {
    if (!enforceAction?.action) return;
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    setEnforcing(true);
    setError("");
    try {
      const res = await fetch(
        `${baseURL}/api/looks/admin/${enforceAction.lookId}/enforce`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            action: enforceAction.action,
            note: enforceNote,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Enforcement failed");
      setEnforceAction(null);
      setEnforceNote("");
      await loadLooks();
      await loadTabCounts();
    } catch (err) {
      setError(err.message || "Enforcement failed");
    } finally {
      setEnforcing(false);
    }
  }, [
    enforceAction,
    enforceNote,
    getAuthHeaders,
    getApiBaseUrl,
    loadLooks,
    loadTabCounts,
  ]);

  const moveQueue = useCallback(
    async (lookId, queueStatus) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      setQueueMovingId(lookId);
      setError("");
      try {
        const res = await fetch(
          `${baseURL}/api/looks/admin/${lookId}/queue-status`,
          {
            method: "PUT",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ queueStatus }),
          },
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to update queue");
        const currentQueueParam = QUEUE_TABS.find(
          (t) => t.key === activeTab,
        )?.queueParam;
        if (currentQueueParam && queueStatus !== currentQueueParam) {
          setLooks((prev) => prev.filter((l) => l._id !== lookId));
        } else {
          await loadLooks();
        }
        await loadTabCounts();
      } catch (err) {
        setError(err.message || "Failed to update queue");
      } finally {
        setQueueMovingId(null);
      }
    },
    [getAuthHeaders, getApiBaseUrl, activeTab, loadLooks, loadTabCounts],
  );

  const respondToAppeal = useCallback(async () => {
    if (!appealDecision) return;
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    setAppealBusy(true);
    setAppealsError("");
    try {
      const res = await fetch(
        `${baseURL}/api/looks/admin/${appealDecision.lookId}/appeal/respond`,
        {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            decision: appealDecision.decision,
            adminResponse: appealResponse,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok)
        throw new Error(data?.message || "Failed to respond to appeal");
      setAppealDecision(null);
      setAppealResponse("");
      await loadAppeals();
      await loadTabCounts();
    } catch (err) {
      setAppealsError(err.message || "Failed to respond to appeal");
    } finally {
      setAppealBusy(false);
    }
  }, [
    appealDecision,
    appealResponse,
    getAuthHeaders,
    getApiBaseUrl,
    loadAppeals,
    loadTabCounts,
  ]);

  const deleteLook = useCallback(
    async (lookId) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      setBusyId(lookId);
      setError("");
      try {
        const res = await fetch(`${baseURL}/api/looks/admin/${lookId}`, {
          method: "DELETE",
          headers,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to delete look");
        setLooks((prev) => prev.filter((l) => l._id !== lookId));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(lookId);
          return next;
        });
        await loadTabCounts();
      } catch (err) {
        setError(err.message || "Failed to delete look");
      } finally {
        setBusyId(null);
      }
    },
    [getAuthHeaders, getApiBaseUrl, loadTabCounts],
  );

  const bulkDeleteLooks = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL || selectedIds.size === 0) return;
    setError("");
    try {
      const res = await fetch(`${baseURL}/api/looks/admin/bulk-delete`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ lookIds: Array.from(selectedIds) }),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data?.message || "Failed to bulk delete looks");
      const deletedIds = new Set(Array.from(selectedIds));
      setLooks((prev) => prev.filter((l) => !deletedIds.has(l._id)));
      setSelectedIds(new Set());
      await loadTabCounts();
    } catch (err) {
      setError(err.message || "Failed to bulk delete looks");
    }
  }, [getAuthHeaders, getApiBaseUrl, selectedIds, loadTabCounts]);

  const permanentlyDeleteLook = useCallback(
    async (lookId) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      setBusyId(lookId);
      setError("");
      try {
        const res = await fetch(
          `${baseURL}/api/looks/admin/${lookId}/permanent`,
          {
            method: "DELETE",
            headers,
          },
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.message || "Failed to permanently delete look");
        setLooks((prev) => prev.filter((l) => l._id !== lookId));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(lookId);
          return next;
        });
        setPermanentDeleteConfirm(null);
        await loadTabCounts();
      } catch (err) {
        setError(err.message || "Failed to permanently delete look");
      } finally {
        setBusyId(null);
      }
    },
    [getAuthHeaders, getApiBaseUrl, loadTabCounts],
  );

  const bulkPermanentlyDeleteLooks = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL || selectedIds.size === 0) return;
    setError("");
    try {
      const res = await fetch(
        `${baseURL}/api/looks/admin/bulk-permanent-delete`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ lookIds: Array.from(selectedIds) }),
        },
      );
      const data = await res.json();
      if (!res.ok)
        throw new Error(
          data?.message || "Failed to bulk permanently delete looks",
        );
      const deletedIds = new Set(Array.from(selectedIds));
      setLooks((prev) => prev.filter((l) => !deletedIds.has(l._id)));
      setSelectedIds(new Set());
      setPermanentDeleteConfirm(null);
      await loadTabCounts();
    } catch (err) {
      setError(err.message || "Failed to bulk permanently delete looks");
    }
  }, [getAuthHeaders, getApiBaseUrl, selectedIds, loadTabCounts]);

  const openDetail = useCallback(
    async (lookId) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      try {
        const res = await fetch(`${baseURL}/api/looks/${lookId}`, { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to load");
        setSelectedLook(data.look);
      } catch (err) {
        alert(err.message);
      }
    },
    [getAuthHeaders, getApiBaseUrl],
  );

  const handleExport = useCallback(() => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;
    fetch(`${baseURL}/api/looks/admin/export`, { headers })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `looks-evidence-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => setError(err.message || "Export failed"));
  }, [getAuthHeaders, getApiBaseUrl]);

  const filteredLooks = looks.filter((l) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (l.bookingSnapshot?.serviceName || "").toLowerCase().includes(q) ||
      (l._id || "").toLowerCase().includes(q) ||
      (l.recordId || "").toLowerCase().includes(q) ||
      (l.status || "").toLowerCase().includes(q)
    );
  });

  const allVisibleSelected =
    filteredLooks.length > 0 &&
    filteredLooks.every((l) => selectedIds.has(l._id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredLooks.map((l) => l._id)));
  };

  const toggleSelect = (lookId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lookId)) next.delete(lookId);
      else next.add(lookId);
      return next;
    });
  };

  const s = {
    page: { maxWidth: "1100px", margin: "0 auto" },
    tabs: {
      display: "flex",
      gap: "0",
      marginBottom: "20px",
      borderBottom: "1px solid #e5e7eb",
      flexWrap: "wrap",
    },
    tab: (active) => ({
      padding: "10px 14px",
      border: "none",
      background: "none",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: active ? "700" : "400",
      color: active ? "#1a1a1a" : "#6b7280",
      borderBottom: active ? "2px solid #1a1a1a" : "2px solid transparent",
      display: "flex",
      alignItems: "center",
      gap: "5px",
      whiteSpace: "nowrap",
    }),
    toolbar: {
      display: "flex",
      gap: "10px",
      alignItems: "center",
      marginBottom: "16px",
      flexWrap: "wrap",
    },
    searchInput: {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "14px",
      width: "220px",
    },
    btn: {
      padding: "8px 14px",
      borderRadius: "6px",
      border: "1px solid #d1d5db",
      background: "#fff",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "13px",
    },
    card: {
      border: "1px solid #e5e7eb",
      borderRadius: "10px",
      background: "#fff",
      padding: "16px",
      marginBottom: "12px",
    },
    cardGrid: {
      display: "grid",
      gridTemplateColumns: "120px 1fr",
      gap: "14px",
      alignItems: "start",
    },
    img: {
      width: "120px",
      height: "120px",
      objectFit: "cover",
      borderRadius: "8px",
      background: "#f3f4f6",
      border: "1px solid #e5e7eb",
    },
    statusBadge: (status) => ({
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "600",
      background: (STATUS_LABELS[status]?.color || "#888") + "22",
      color: STATUS_LABELS[status]?.color || "#374151",
    }),
    queueBadge: (qs) => ({
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "600",
      background: QUEUE_STATUS_LABELS[qs]?.bg || "#f9fafb",
      color: QUEUE_STATUS_LABELS[qs]?.color || "#374151",
    }),
    mini: (color, bg) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "600",
      background: bg,
      color,
    }),
    reportedBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "600",
      background: "#fee2e2",
      color: "#b91c1c",
    },
    actions: {
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
      marginTop: "10px",
      alignItems: "flex-start",
    },
    ab: (color, bg, border) => ({
      padding: "6px 12px",
      borderRadius: "6px",
      border: `1px solid ${border}`,
      background: bg,
      color,
      fontSize: "12px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    }),
    select: {
      padding: "6px 8px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "12px",
      cursor: "pointer",
      background: "#fff",
    },
    detailModal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      zIndex: 1000,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      overflowY: "auto",
      padding: "40px 16px",
    },
    detailBox: {
      background: "#fff",
      borderRadius: "10px",
      padding: "24px",
      maxWidth: "640px",
      width: "100%",
    },
  };

  return (
    <PortalAdminLayout>
      <div style={s.page}>
        <h1 style={{ marginBottom: "4px" }}>Looks Governance</h1>
        <p style={{ color: "#6b7280", marginTop: 0, fontSize: "14px" }}>
          Moderate reported looks, manage the review queue, handle appeals, and
          maintain platform compliance.
        </p>

        {/* Tabs */}
        <div style={s.tabs}>
          {QUEUE_TABS.map((t) => {
            const badgeCount = tabCounts[t.key] || 0;
            return (
              <button
                key={t.key}
                style={s.tab(activeTab === t.key)}
                onClick={() => {
                  setActiveTab(t.key);
                  setSearch("");
                }}
              >
                {t.icon} {t.label}
                {badgeCount > 0 && (
                  <span
                    style={{
                      background: t.key === "escalated" ? "#7c3aed" : "#ef4444",
                      color: "#fff",
                      borderRadius: "10px",
                      padding: "1px 6px",
                      fontSize: "11px",
                      marginLeft: "2px",
                    }}
                  >
                    {badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        {isLooksTab && (
          <>
            {activeTab === "deleted" && (
              <div
                style={{
                  marginBottom: "10px",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  background: "#fef3c7",
                  color: "#92400e",
                  border: "1px solid #fcd34d",
                  fontSize: "13px",
                }}
              >
                ⏳ Deleted looks are kept for 30 days and then permanently
                removed.
              </div>
            )}
            <div style={s.toolbar}>
              <input
                style={s.searchInput}
                placeholder="Search by service, look ID, record ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                style={{ ...s.select, padding: "8px 10px", fontSize: "13px" }}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date">Sort: Latest</option>
                <option value="priority">Sort: Priority</option>
              </select>
              {filteredLooks.length > 0 && (
                <button style={s.btn} onClick={toggleSelectAll}>
                  {allVisibleSelected
                    ? "Deselect All"
                    : `Select All (${filteredLooks.length})`}
                </button>
              )}
              {selectedIds.size > 0 && activeTab !== "deleted" && (
                <button
                  style={{
                    ...s.btn,
                    borderColor: "#fca5a5",
                    color: "#991b1b",
                    background: "#fef2f2",
                  }}
                  onClick={bulkDeleteLooks}
                >
                  <FaTrash /> Delete Selected ({selectedIds.size})
                </button>
              )}
              {selectedIds.size > 0 && (
                <button
                  style={{
                    ...s.btn,
                    borderColor: "#991b1b",
                    color: "#fff",
                    background: "#991b1b",
                  }}
                  onClick={() =>
                    setPermanentDeleteConfirm({
                      type: "bulk",
                      lookIds: Array.from(selectedIds),
                    })
                  }
                >
                  <FaTrash /> Permanently Delete ({selectedIds.size})
                </button>
              )}
              <button style={s.btn} onClick={loadLooks} disabled={loading}>
                <FaRedo /> Refresh
              </button>
              <button style={s.btn} onClick={handleExport}>
                <FaDownload /> Export Evidence
              </button>
            </div>
          </>
        )}

        {error && (
          <div
            style={{
              marginBottom: "14px",
              padding: "10px 14px",
              borderRadius: "8px",
              background: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #fecaca",
            }}
          >
            {error}
          </div>
        )}

        {isLooksTab && loading && <p style={{ color: "#6b7280" }}>Loading…</p>}

        {isLooksTab && !loading && filteredLooks.length === 0 && (
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "32px",
              textAlign: "center",
              color: "#9ca3af",
              background: "#fff",
            }}
          >
            {activeTab === "reported"
              ? "No new reports waiting for review."
              : activeTab === "under_review"
                ? "No looks currently under review."
                : activeTab === "escalated"
                  ? "No escalated cases."
                  : activeTab === "resolved"
                    ? "No resolved cases."
                    : activeTab === "deleted"
                      ? "No deleted looks. Deleted looks are permanently removed after 30 days."
                      : "No looks found."}
          </div>
        )}

        {/* ── Looks Queue Cards ── */}
        {isLooksTab &&
          !loading &&
          filteredLooks.map((look) => {
            const img = look.images?.[0]?.url || look.image?.url;
            const isReported =
              look.reporting?.isReported ||
              look.reporting?.isHiddenPendingReview;
            const isBusy = busyId === look._id;
            const isSelected = selectedIds.has(look._id);
            const showEnforce = enforceAction?.lookId === look._id;
            const sla = getSlaInfo(look.moderation?.slaDueAt);
            const hasPreflag = (look.reporting?.preFlagged?.score || 0) > 0;

            return (
              <div
                key={look._id}
                style={{
                  ...s.card,
                  borderColor:
                    look.moderation?.queueStatus === "escalated"
                      ? "#c4b5fd"
                      : "#e5e7eb",
                }}
              >
                <div style={s.cardGrid}>
                  {img ? (
                    <img src={img} alt="Look" style={s.img} />
                  ) : (
                    <div
                      style={{
                        ...s.img,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#9ca3af",
                        fontSize: "12px",
                      }}
                    >
                      No image
                    </div>
                  )}
                  <div>
                    {/* Badge row */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        flexWrap: "wrap",
                        marginBottom: "6px",
                      }}
                    >
                      <label
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "12px",
                          color: "#374151",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(look._id)}
                        />
                        Select
                      </label>
                      <span style={s.statusBadge(look.status)}>
                        {STATUS_LABELS[look.status]?.label || look.status}
                      </span>
                      {look.moderation?.queueStatus && (
                        <span style={s.queueBadge(look.moderation.queueStatus)}>
                          {
                            QUEUE_STATUS_LABELS[look.moderation.queueStatus]
                              ?.label
                          }
                        </span>
                      )}
                      {isReported && (
                        <span style={s.reportedBadge}>
                          <FaFlag size={9} /> {look.reporting?.reportCount || 1}{" "}
                          report(s)
                        </span>
                      )}
                      {sla && (
                        <span style={s.mini(sla.color, sla.color + "22")}>
                          {sla.isOverdue ? (
                            <FaExclamationTriangle size={9} />
                          ) : (
                            <FaClock size={9} />
                          )}{" "}
                          SLA: {sla.label}
                        </span>
                      )}
                      {hasPreflag && (
                        <span style={s.mini("#7c3aed", "#f5f3ff")}>
                          <FaExclamationTriangle size={9} /> Pre-flagged
                        </span>
                      )}
                    </div>

                    <div style={{ fontWeight: "600", fontSize: "14px" }}>
                      {look.bookingSnapshot?.serviceName || "Service"}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "2px",
                      }}
                    >
                      {look.bookingSnapshot?.appointmentDate || ""}
                      {look.bookingSnapshot?.appointmentDate ? " · " : ""}
                      Consent: {look.consent?.status || "pending"}
                    </div>
                    {isReported && look.reporting?.reports?.length > 0 && (
                      <div
                        style={{
                          marginTop: "6px",
                          padding: "6px 10px",
                          background: "#fef2f2",
                          borderRadius: "6px",
                          fontSize: "12px",
                          color: "#7f1d1d",
                        }}
                      >
                        Latest:{" "}
                        {look.reporting.reports[
                          look.reporting.reports.length - 1
                        ]?.reason || "(no reason)"}
                      </div>
                    )}
                    {hasPreflag && (
                      <div
                        style={{
                          marginTop: "4px",
                          padding: "4px 10px",
                          background: "#f5f3ff",
                          borderRadius: "6px",
                          fontSize: "11px",
                          color: "#5b21b6",
                        }}
                      >
                        Signals: {look.reporting.preFlagged.signals.join(", ")}{" "}
                        · score: {look.reporting.preFlagged.score}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}
                    >
                      ID: {look.recordId || look._id}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginTop: "2px",
                      }}
                    >
                      Unique views: {(look.analytics?.viewCount || 0).toLocaleString()} · Clicks: {(look.analytics?.clickCount || 0).toLocaleString()} · Ratio: {look.analytics?.viewCount > 0 ? `${((look.analytics?.clickCount || 0) / look.analytics.viewCount).toFixed(1)}x` : "1.0x"}
                    </div>

                    <div style={s.actions}>
                      <button
                        style={s.ab("#374151", "#f9fafb", "#d1d5db")}
                        onClick={() => openDetail(look._id)}
                      >
                        <FaEye size={11} /> View
                      </button>
                      {activeTab !== "deleted" && (
                        <button
                          style={s.ab("#991b1b", "#fef2f2", "#fecaca")}
                          disabled={isBusy}
                          onClick={() => deleteLook(look._id)}
                        >
                          <FaTrash size={11} /> Delete
                        </button>
                      )}
                      <button
                        style={s.ab("#fff", "#991b1b", "#7f1d1d")}
                        disabled={isBusy}
                        onClick={() =>
                          setPermanentDeleteConfirm({
                            type: "single",
                            lookId: look._id,
                          })
                        }
                      >
                        <FaTrash size={11} /> Permanent
                      </button>

                      {/* Queue move dropdown */}
                      <select
                        style={s.select}
                        value=""
                        onChange={(e) => {
                          if (e.target.value)
                            moveQueue(look._id, e.target.value);
                        }}
                        disabled={queueMovingId === look._id}
                      >
                        <option value="">Move queue…</option>
                        {QUEUE_MOVE_OPTIONS.filter(
                          (o) => o.value !== look.moderation?.queueStatus,
                        ).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>

                      {isReported && (
                        <>
                          <textarea
                            value={reasons[look._id] || ""}
                            onChange={(e) =>
                              setReasons((prev) => ({
                                ...prev,
                                [look._id]: e.target.value,
                              }))
                            }
                            placeholder="Reason (required for Remove)"
                            rows={1}
                            style={{
                              padding: "6px 8px",
                              borderRadius: "6px",
                              border: "1px solid #d1d5db",
                              fontSize: "12px",
                              resize: "vertical",
                              width: "180px",
                            }}
                          />
                          <button
                            style={s.ab("#166534", "#f0fdf4", "#86efac")}
                            disabled={isBusy}
                            onClick={() => moderateLook(look._id, "restore")}
                          >
                            <FaCheckCircle size={11} /> Restore
                          </button>
                          <button
                            style={s.ab("#991b1b", "#fef2f2", "#fecaca")}
                            disabled={isBusy}
                            onClick={() => moderateLook(look._id, "remove")}
                          >
                            <FaTrash size={11} /> Remove
                          </button>
                        </>
                      )}
                      <button
                        style={s.ab("#4338ca", "#eef2ff", "#c7d2fe")}
                        onClick={() =>
                          setEnforceAction(
                            showEnforce
                              ? null
                              : { lookId: look._id, action: "" },
                          )
                        }
                      >
                        <FaShieldAlt size={11} /> Enforce…
                      </button>
                    </div>

                    {showEnforce && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "14px",
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            marginBottom: "4px",
                          }}
                        >
                          Enforcement action
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "400",
                              color: "#6b7280",
                              marginLeft: "8px",
                            }}
                          >
                            Sanction ladder auto-applies
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            marginBottom: "10px",
                          }}
                        >
                          {ENFORCE_ACTIONS.map((a) => (
                            <button
                              key={a.key}
                              style={{
                                ...s.ab(a.color, a.bg, a.border),
                                outline:
                                  enforceAction.action === a.key
                                    ? `2px solid ${a.border}`
                                    : "none",
                              }}
                              onClick={() =>
                                setEnforceAction((prev) => ({
                                  ...prev,
                                  action: a.key,
                                }))
                              }
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                        <textarea
                          placeholder="Admin note (optional)"
                          rows={2}
                          value={enforceNote}
                          onChange={(e) => setEnforceNote(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            resize: "vertical",
                            marginBottom: "8px",
                            boxSizing: "border-box",
                          }}
                        />
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            style={s.ab("#374151", "#f9fafb", "#d1d5db")}
                            onClick={() => {
                              setEnforceAction(null);
                              setEnforceNote("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            style={s.ab("#fff", "#1a1a1a", "#1a1a1a")}
                            disabled={!enforceAction.action || enforcing}
                            onClick={applyEnforcement}
                          >
                            {enforcing ? "Applying…" : "Apply Action"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

        {/* ── Appeals Tab ── */}
        {activeTab === "appeals" && (
          <div>
            {appealsLoading && (
              <p style={{ color: "#6b7280" }}>Loading appeals…</p>
            )}
            {appealsError && (
              <div
                style={{
                  marginBottom: "14px",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  background: "#fef2f2",
                  color: "#991b1b",
                  border: "1px solid #fecaca",
                }}
              >
                {appealsError}
              </div>
            )}
            {!appealsLoading && appeals.length === 0 && (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "32px",
                  textAlign: "center",
                  color: "#9ca3af",
                  background: "#fff",
                }}
              >
                No appeals found.
              </div>
            )}
            {!appealsLoading &&
              appeals.map((look) => {
                const img = look.images?.[0]?.url || look.image?.url;
                const ap = look.appeal || {};
                const apLabel = APPEAL_STATUS_LABELS[ap.status] || {};
                const showRespond = appealDecision?.lookId === look._id;

                return (
                  <div key={look._id} style={s.card}>
                    <div style={s.cardGrid}>
                      {img ? (
                        <img src={img} alt="Look" style={s.img} />
                      ) : (
                        <div
                          style={{
                            ...s.img,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#9ca3af",
                            fontSize: "12px",
                          }}
                        >
                          No image
                        </div>
                      )}
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginBottom: "6px",
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={s.statusBadge(look.status)}>
                            {STATUS_LABELS[look.status]?.label || look.status}
                          </span>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "11px",
                              fontWeight: "600",
                              background: apLabel.bg || "#f9fafb",
                              color: apLabel.color || "#374151",
                            }}
                          >
                            Appeal: {apLabel.label || ap.status}
                          </span>
                          {ap.status === "pending" && (
                            <span style={s.mini("#f0a500", "#fffbeb")}>
                              <FaClock size={9} /> Awaiting response
                            </span>
                          )}
                        </div>
                        <div style={{ fontWeight: "600", fontSize: "14px" }}>
                          {look.bookingSnapshot?.serviceName || "Service"}
                        </div>
                        {look.providerName && (
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            Provider: {look.providerName}
                            {look.providerEmail
                              ? ` · ${look.providerEmail}`
                              : ""}
                          </div>
                        )}
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          Submitted:{" "}
                          {ap.submittedAt
                            ? new Date(ap.submittedAt).toLocaleString()
                            : "—"}
                        </div>
                        {ap.reason && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              background: "#f9fafb",
                              border: "1px solid #e5e7eb",
                              borderRadius: "6px",
                              fontSize: "12px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "600",
                                color: "#374151",
                                marginBottom: "2px",
                              }}
                            >
                              Provider's reason:
                            </div>
                            <div style={{ color: "#374151" }}>{ap.reason}</div>
                          </div>
                        )}
                        {ap.adminResponse && (
                          <div
                            style={{
                              marginTop: "6px",
                              padding: "8px 12px",
                              background:
                                ap.status === "approved"
                                  ? "#f0fdf4"
                                  : "#fef2f2",
                              border: `1px solid ${ap.status === "approved" ? "#86efac" : "#fecaca"}`,
                              borderRadius: "6px",
                              fontSize: "12px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "600",
                                color:
                                  ap.status === "approved"
                                    ? "#166534"
                                    : "#991b1b",
                                marginBottom: "2px",
                              }}
                            >
                              Admin response:
                            </div>
                            <div>{ap.adminResponse}</div>
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            marginTop: "4px",
                          }}
                        >
                          Look ID: {look._id}
                        </div>

                        {ap.status === "pending" && (
                          <div style={s.actions}>
                            <button
                              style={s.ab("#374151", "#f9fafb", "#d1d5db")}
                              onClick={() => openDetail(look._id)}
                            >
                              <FaEye size={11} /> View Look
                            </button>
                            <button
                              style={s.ab("#166534", "#f0fdf4", "#86efac")}
                              onClick={() => {
                                setAppealDecision({
                                  lookId: look._id,
                                  decision: "approved",
                                });
                                setAppealResponse("");
                              }}
                            >
                              <FaCheck size={11} /> Approve
                            </button>
                            <button
                              style={s.ab("#991b1b", "#fef2f2", "#fecaca")}
                              onClick={() => {
                                setAppealDecision({
                                  lookId: look._id,
                                  decision: "denied",
                                });
                                setAppealResponse("");
                              }}
                            >
                              <FaTimes size={11} /> Deny
                            </button>
                          </div>
                        )}

                        {showRespond && (
                          <div
                            style={{
                              marginTop: "10px",
                              padding: "14px",
                              background:
                                appealDecision.decision === "approved"
                                  ? "#f0fdf4"
                                  : "#fef2f2",
                              border: `1px solid ${appealDecision.decision === "approved" ? "#86efac" : "#fecaca"}`,
                              borderRadius: "8px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                marginBottom: "8px",
                              }}
                            >
                              {appealDecision.decision === "approved"
                                ? "Approve — look will be restored"
                                : "Deny — sanction remains in place"}
                            </div>
                            <textarea
                              placeholder="Response to provider (recommended)"
                              rows={3}
                              value={appealResponse}
                              onChange={(e) =>
                                setAppealResponse(e.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "8px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                                resize: "vertical",
                                marginBottom: "8px",
                                boxSizing: "border-box",
                              }}
                            />
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                style={s.ab("#374151", "#f9fafb", "#d1d5db")}
                                onClick={() => setAppealDecision(null)}
                              >
                                Cancel
                              </button>
                              <button
                                style={s.ab(
                                  "#fff",
                                  appealDecision.decision === "approved"
                                    ? "#16a34a"
                                    : "#dc2626",
                                  appealDecision.decision === "approved"
                                    ? "#16a34a"
                                    : "#dc2626",
                                )}
                                disabled={appealBusy}
                                onClick={respondToAppeal}
                              >
                                {appealBusy
                                  ? "Saving…"
                                  : appealDecision.decision === "approved"
                                    ? "Confirm Approval"
                                    : "Confirm Denial"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* ── Provider Directory Tab ── */}
        {activeTab === "providers" && (
          <div>
            {providersLoading && (
              <p style={{ color: "#6b7280" }}>Loading provider directory…</p>
            )}
            {providersError && (
              <div
                style={{
                  marginBottom: "14px",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  background: "#fef2f2",
                  color: "#991b1b",
                  border: "1px solid #fecaca",
                }}
              >
                {providersError}
              </div>
            )}
            {!providersLoading && !providersError && providers.length === 0 && (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "32px",
                  textAlign: "center",
                  color: "#9ca3af",
                  background: "#fff",
                }}
              >
                No provider data available.
              </div>
            )}
            {!providersLoading && providers.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#f9fafb",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      {[
                        "Provider",
                        "Role",
                        "Total",
                        "Published",
                        "Reports",
                        "Sanctions",
                        "Risk",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "10px 12px",
                            textAlign: "left",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {providers.map((p) => {
                      const riskColor =
                        p.riskLevel === "high"
                          ? "#ef4444"
                          : p.riskLevel === "medium"
                            ? "#f0a500"
                            : "#22c55e";
                      const riskBg =
                        p.riskLevel === "high"
                          ? "#fee2e2"
                          : p.riskLevel === "medium"
                            ? "#fffbeb"
                            : "#f0fdf4";
                      return (
                        <tr
                          key={p.staffId}
                          style={{ borderBottom: "1px solid #f3f4f6" }}
                        >
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ fontWeight: "600" }}>
                              {p.name || p.staffId}
                            </div>
                            {p.email && (
                              <div
                                style={{ fontSize: "11px", color: "#9ca3af" }}
                              >
                                {p.email}
                              </div>
                            )}
                          </td>
                          <td
                            style={{ padding: "10px 12px", color: "#6b7280" }}
                          >
                            {p.role || "—"}
                          </td>
                          <td style={{ padding: "10px 12px" }}>{p.total}</td>
                          <td
                            style={{
                              padding: "10px 12px",
                              color: "#22c55e",
                              fontWeight: "600",
                            }}
                          >
                            {p.published}
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              color: p.reported > 0 ? "#ef4444" : "#374151",
                              fontWeight: p.reported > 0 ? "700" : "400",
                            }}
                          >
                            {p.reported}
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              color: p.sanctions > 0 ? "#b91c1c" : "#374151",
                              fontWeight: p.sanctions > 0 ? "700" : "400",
                            }}
                          >
                            {p.sanctions}
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 10px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: "700",
                                background: riskBg,
                                color: riskColor,
                              }}
                            >
                              {p.riskLevel?.toUpperCase() || "—"}
                            </span>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#9ca3af",
                                marginTop: "2px",
                              }}
                            >
                              score: {p.riskScore}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Analytics Tab ── */}
        {activeTab === "analytics" && (
          <div>
            {analyticsLoading && (
              <p style={{ color: "#6b7280" }}>Loading analytics…</p>
            )}
            {analyticsError && (
              <div
                style={{
                  marginBottom: "14px",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  background: "#fef2f2",
                  color: "#991b1b",
                  border: "1px solid #fecaca",
                }}
              >
                {analyticsError}
              </div>
            )}
            {!analyticsLoading && analytics && (
              <div>
                <div
                  style={{
                    display: "flex",
                    gap: "14px",
                    flexWrap: "wrap",
                    marginBottom: "28px",
                  }}
                >
                  {[
                    {
                      label: "Total Looks",
                      value: analytics.overview?.totalLooks ?? "—",
                    },
                    {
                      label: "Published",
                      value: analytics.overview?.publishedLooks ?? "—",
                    },
                    {
                      label: "Reported",
                      value: analytics.overview?.reportedLooks ?? "—",
                      danger: analytics.overview?.reportedLooks > 0,
                    },
                    {
                      label: "Hidden Pending",
                      value: analytics.overview?.hiddenPending ?? "—",
                      danger: analytics.overview?.hiddenPending > 0,
                    },
                    {
                      label: "Archived",
                      value: analytics.overview?.archivedLooks ?? "—",
                    },
                    {
                      label: "Publish Rate",
                      value:
                        analytics.overview?.publishRate != null
                          ? `${analytics.overview.publishRate}%`
                          : "—",
                    },
                    {
                      label: "Report Rate",
                      value:
                        analytics.overview?.reportRate != null
                          ? `${analytics.overview.reportRate}%`
                          : "—",
                      danger: analytics.overview?.reportRate > 5,
                    },
                  ].map(({ label, value, danger }) => (
                    <div
                      key={label}
                      style={{
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "14px 20px",
                        minWidth: "110px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "700",
                          color: danger ? "#ef4444" : "#1a1a1a",
                        }}
                      >
                        {value}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginTop: "2px",
                        }}
                      >
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
                {analytics.consent && (
                  <div style={{ marginBottom: "28px" }}>
                    <h3
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        marginBottom: "12px",
                      }}
                    >
                      Consent Status
                    </h3>
                    <div
                      style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
                    >
                      {[
                        {
                          label: "Approved",
                          value: analytics.consent.approved,
                          color: "#22c55e",
                        },
                        {
                          label: "Pending",
                          value: analytics.consent.pending,
                          color: "#f0a500",
                        },
                        {
                          label: "Declined",
                          value: analytics.consent.declined,
                          color: "#ef4444",
                        },
                        {
                          label: "Approval Rate",
                          value: `${analytics.consent.approvalRate ?? "—"}%`,
                          color: "#3b82f6",
                        },
                      ].map(({ label, value, color }) => (
                        <div
                          key={label}
                          style={{
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            padding: "12px 18px",
                            textAlign: "center",
                            minWidth: "100px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "22px",
                              fontWeight: "700",
                              color,
                            }}
                          >
                            {value}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            {label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                  }}
                >
                  {analytics.enforcementBreakdown?.length > 0 && (
                    <div>
                      <h3
                        style={{
                          fontSize: "15px",
                          fontWeight: "700",
                          marginBottom: "12px",
                        }}
                      >
                        Enforcement Actions
                      </h3>
                      <div
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        {analytics.enforcementBreakdown.map((row, i) => (
                          <div
                            key={row._id || i}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "9px 14px",
                              background: i % 2 === 0 ? "#fff" : "#f9fafb",
                              fontSize: "13px",
                            }}
                          >
                            <span style={{ color: "#374151" }}>
                              {(row._id || "").replace(/_/g, " ")}
                            </span>
                            <span
                              style={{ fontWeight: "700", color: "#1a1a1a" }}
                            >
                              {row.count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {analytics.reportTrend?.length > 0 && (
                    <div>
                      <h3
                        style={{
                          fontSize: "15px",
                          fontWeight: "700",
                          marginBottom: "12px",
                        }}
                      >
                        Reports (Last 30 Days)
                      </h3>
                      <div
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          overflow: "hidden",
                          maxHeight: "240px",
                          overflowY: "auto",
                        }}
                      >
                        {analytics.reportTrend.map((row, i) => (
                          <div
                            key={row._id || i}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "7px 14px",
                              background: i % 2 === 0 ? "#fff" : "#f9fafb",
                              fontSize: "13px",
                            }}
                          >
                            <span style={{ color: "#374151" }}>
                              {row._id || "—"}
                            </span>
                            <span
                              style={{
                                fontWeight: "700",
                                color: row.count > 0 ? "#ef4444" : "#374151",
                              }}
                            >
                              {row.count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      {selectedLook && (
        <div style={s.detailModal} onClick={() => setSelectedLook(null)}>
          <div style={s.detailBox} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "16px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px" }}>Look Details</h2>
              <button
                style={{
                  padding: "5px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                  background: "#fff",
                }}
                onClick={() => setSelectedLook(null)}
              >
                Close
              </button>
            </div>
            {selectedLook.images?.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  marginBottom: "16px",
                }}
              >
                {selectedLook.images.map((img, i) => (
                  <img
                    key={i}
                    src={img.url}
                    alt={`Look ${i + 1}`}
                    style={{
                      width: "140px",
                      height: "140px",
                      objectFit: "cover",
                      borderRadius: "6px",
                    }}
                  />
                ))}
              </div>
            )}
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
              }}
            >
              <tbody>
                {[
                  [
                    "Status",
                    <span style={s.statusBadge(selectedLook.status)}>
                      {STATUS_LABELS[selectedLook.status]?.label ||
                        selectedLook.status}
                    </span>,
                  ],
                  [
                    "Queue",
                    selectedLook.moderation?.queueStatus ? (
                      <span
                        style={s.queueBadge(
                          selectedLook.moderation.queueStatus,
                        )}
                      >
                        {
                          QUEUE_STATUS_LABELS[
                            selectedLook.moderation.queueStatus
                          ]?.label
                        }
                      </span>
                    ) : (
                      "—"
                    ),
                  ],
                  [
                    "SLA Due",
                    selectedLook.moderation?.slaDueAt
                      ? new Date(
                          selectedLook.moderation.slaDueAt,
                        ).toLocaleString()
                      : "—",
                  ],
                  ["Consent", selectedLook.consent?.status || "—"],
                  [
                    "Moderation",
                    `${selectedLook.moderation?.status || "—"}${selectedLook.moderation?.reason ? ` — ${selectedLook.moderation.reason}` : ""}`,
                  ],
                  [
                    "Appeal",
                    selectedLook.appeal?.status &&
                    selectedLook.appeal.status !== "none"
                      ? selectedLook.appeal.status
                      : "None",
                  ],
                  ["Service", selectedLook.bookingSnapshot?.serviceName || "—"],
                  [
                    "Date",
                    selectedLook.bookingSnapshot?.appointmentDate || "—",
                  ],
                  [
                    "Visibility",
                    `Shop: ${selectedLook.visibility?.showOnShop ? "✓" : "✗"}  Profile: ${selectedLook.visibility?.showOnProfessional ? "✓" : "✗"}  Public: ${selectedLook.visibility?.isPublic ? "✓" : "✗"}`,
                  ],
                  ["Reports", selectedLook.reporting?.reportCount || 0],
                  [
                    "Unique Views",
                    (selectedLook.analytics?.viewCount || 0).toLocaleString(),
                  ],
                  [
                    "Total Clicks",
                    (selectedLook.analytics?.clickCount || 0).toLocaleString(),
                  ],
                  [
                    "Click/Unique Ratio",
                    selectedLook.analytics?.viewCount > 0
                      ? `${((selectedLook.analytics?.clickCount || 0) / selectedLook.analytics.viewCount).toFixed(1)}x`
                      : "1.0x",
                  ],
                  [
                    "Pre-flag Score",
                    selectedLook.reporting?.preFlagged?.score ?? 0,
                  ],
                  ["Record ID", selectedLook.recordId || selectedLook._id],
                  ["Look ID", selectedLook._id],
                  [
                    "Consent Approved At",
                    selectedLook.consent?.approvedAt
                      ? new Date(selectedLook.consent.approvedAt).toLocaleString()
                      : "—",
                  ],
                  [
                    "Created",
                    selectedLook.createdAt
                      ? new Date(selectedLook.createdAt).toLocaleString()
                      : "—",
                  ],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <td
                      style={{
                        padding: "6px 0",
                        color: "#6b7280",
                        width: "130px",
                        verticalAlign: "top",
                      }}
                    >
                      {label}
                    </td>
                    <td style={{ padding: "6px 0" }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {selectedLook.reporting?.reports?.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    marginBottom: "8px",
                  }}
                >
                  Reports ({selectedLook.reporting.reports.length})
                </div>
                {selectedLook.reporting.reports.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "10px",
                      marginBottom: "6px",
                      background: "#fef2f2",
                      borderRadius: "6px",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ fontWeight: "600", color: "#7f1d1d" }}>
                      {r.reason || "(no reason)"}
                    </div>
                    {r.details && (
                      <div style={{ color: "#6b7280", marginTop: "3px" }}>
                        {r.details}
                      </div>
                    )}
                    <div style={{ color: "#9ca3af", marginTop: "3px" }}>
                      {r.createdAt
                        ? new Date(r.createdAt).toLocaleString()
                        : ""}
                      {r.reporterRole ? ` · ${r.reporterRole}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedLook.auditTrail?.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    marginBottom: "10px",
                  }}
                >
                  Audit Trail
                </div>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    borderLeft: "2px solid #e5e7eb",
                    paddingLeft: "14px",
                  }}
                >
                  {[...selectedLook.auditTrail].reverse().map((entry, i) => (
                    <div
                      key={i}
                      style={{ marginBottom: "10px", position: "relative" }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: "-19px",
                          top: "5px",
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: ["R6", "R1"].includes(entry.actorRole)
                            ? "#4338ca"
                            : "#6b7280",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        {entry.event.replace(/_/g, " ")}
                        {entry.metadata?.autoEscalated
                          ? " [auto-escalated]"
                          : ""}
                      </div>
                      <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                        {entry.createdAt
                          ? new Date(entry.createdAt).toLocaleString()
                          : ""}
                        {entry.actorRole ? ` · ${entry.actorRole}` : ""}
                        {entry.metadata?.note
                          ? ` — "${entry.metadata.note}"`
                          : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {permanentDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "white",
              padding: "30px",
              borderRadius: "12px",
              maxWidth: "500px",
              width: "90%",
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: "16px",
                color: "#991b1b",
                fontSize: "20px",
                fontWeight: "700",
              }}
            >
              <FaExclamationTriangle style={{ marginRight: "8px" }} />
              Permanent Delete Warning
            </h3>
            <p
              style={{
                margin: 0,
                marginBottom: "16px",
                fontSize: "15px",
                color: "#374151",
                lineHeight: "1.6",
              }}
            >
              {permanentDeleteConfirm.type === "single"
                ? "You are about to permanently delete this look. This action cannot be undone. The look and all associated data will be permanently removed from the database."
                : `You are about to permanently delete ${permanentDeleteConfirm.lookIds.length} looks. This action cannot be undone. All selected looks and their associated data will be permanently removed from the database.`}
            </p>
            <p
              style={{
                margin: 0,
                marginBottom: "24px",
                padding: "12px",
                background: "#fef2f2",
                borderLeft: "3px solid #991b1b",
                fontSize: "14px",
                color: "#7f1d1d",
              }}
            >
              <strong>⚠️ This cannot be recovered.</strong> Ensure you have
              documented any evidence needed before proceeding.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setPermanentDeleteConfirm(null)}
                style={{
                  padding: "10px 20px",
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (permanentDeleteConfirm.type === "single") {
                    permanentlyDeleteLook(permanentDeleteConfirm.lookId);
                  } else {
                    bulkPermanentlyDeleteLooks();
                  }
                }}
                style={{
                  padding: "10px 20px",
                  background: "#991b1b",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "white",
                }}
              >
                Yes, Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PortalAdminLayout>
  );
}

export default LooksModeration;
